import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { BaseService } from "../../core/base.service";
import { environment } from "../../../environments/environment";

// ============================================
//  Entity & Option interfaces
// ============================================

/** 作业反馈实体（对应后端 homework_feedback 表） */
export interface HomeworkFeedback {
  id: number;
  studentId: number;
  studentName: string;
  homeworkContent: string;
  homeworkDate: string;
  status: string;
  statusId?: number;
  teacherId?: number;
  semesterId?: number;
  /** 关联的布置作业ID（homework_assignment.id），NULL/缺省 = 独立记录 */
  assignmentId?: number;
  remarks?: string;
  /** 图片URL（多个以英文逗号分隔），如 "/Daemon/xxx/yyy.jpg,/Daemon/xxx/zzz.jpg" */
  images?: string;
  addTime?: string;
  addUserId?: number;
}

/** 逗号分隔的图片URL串 → URL数组（兼容空 / 未定义 / NULL） */
export function splitHomeworkImages(images?: string | null): string[] {
  if (!images) return [];
  return images
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 作业状态类型字典项 */
export interface HomeworkFeedbackTypeDict {
  id: number;
  value: string;
  meaning: string;
  remark: string;
  isSystem?: number;
}

/** 作业状态类型选项 */
export interface HomeworkFeedbackTypeOption {
  value: string;
  label: string;
  dictId: number;
  isSystem?: number;
}

// ============================================
//  Service
// ============================================

@Injectable({ providedIn: "root" })
export class HomeworkFeedbackService extends BaseService<HomeworkFeedback> {
  private readonly dictBase = `${environment.apiBaseUrl}/AdminDictionary`;

  constructor() {
    super("homeworkFeedback");
  }

  // ========== 作业状态类型管理（AdminDictionary API） ==========

  getStatusTypes(): Observable<HomeworkFeedbackTypeOption[]> {
    return this.http
      .get<{ items: HomeworkFeedbackTypeDict[] }>(`${this.dictBase}/GetHomeworkFeedbackTypes`)
      .pipe(
        map((res) =>
          (res?.items || []).map((d, i) => ({
            value: d.value || String(i),
            label: d.meaning || d.value || "未命名",
            dictId: d.id,
            isSystem: d.isSystem,
          })),
        ),
      );
  }

  addStatusType(value: string, label: string): Observable<HomeworkFeedbackTypeDict> {
    return this.http
      .post<{ items: HomeworkFeedbackTypeDict[] }>(`${this.dictBase}/AddHomeworkFeedbackType`, {
        value,
        meaning: label,
      })
      .pipe(map((res) => res?.items?.[0] || ({} as HomeworkFeedbackTypeDict)));
  }

  updateStatusType(dictId: number, value: string, label: string): Observable<HomeworkFeedbackTypeDict> {
    return this.http
      .put<{ items: HomeworkFeedbackTypeDict[] }>(`${this.dictBase}/UpdateHomeworkFeedbackType`, {
        id: dictId,
        value,
        meaning: label,
      })
      .pipe(map((res) => res?.items?.[0] || ({} as HomeworkFeedbackTypeDict)));
  }

  deleteStatusType(dictId: number): Observable<unknown> {
    return this.http.post(`${this.dictBase}/DeleteHomeworkFeedbackType?id=${dictId}`, null);
  }

  // ========== 关联布置作业的生成记录 ==========

  /** 查询某条布置作业关联生成的记作业记录（@filter=eq(assignmentId,..)） */
  getByAssignment(assignmentId: number): Observable<HomeworkFeedback[]> {
    return this.getEntityList({ "@filter": `eq(assignmentId,${assignmentId})` });
  }
}
